/**
 * Super Admin Module API - Profiles
 * 
 * Database operations for creating and managing user profiles.
 * Used for fixing orphaned auth.users entries and tenant assignments.
 */

import { supabase } from '@/integrations/supabase/client';
import type { CreateProfileInput, AssignTenantInput } from '../types';

// =============================================================================
// Types
// =============================================================================

interface Profile {
  id: string;
  email: string;
  full_name: string | null;
  is_super_admin: boolean;
  created_at: string;
}

interface TenantMembership {
  id: string;
  tenant_id: string;
  user_id: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

// =============================================================================
// Queries
// =============================================================================

/**
 * Checks if a profile exists for a given user ID.
 */
export async function profileExists(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .schema('core')
    .from('profiles')
    .select('id')
    .eq('id', userId)
    .maybeSingle();

  if (error) throw error;
  return data !== null;
}

/**
 * Fetches a profile by user ID.
 */
export async function fetchProfileById(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .schema('core')
    .from('profiles')
    .select('id, email, full_name, is_super_admin, created_at')
    .eq('id', userId)
    .maybeSingle();

  if (error) throw error;
  return data as Profile | null;
}

/**
 * Fetches tenant memberships for a user.
 */
export async function fetchUserTenantMemberships(userId: string): Promise<TenantMembership[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('tenant_memberships')
    .select('id, tenant_id, user_id, status, created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as TenantMembership[];
}

// =============================================================================
// Mutations
// =============================================================================

/**
 * Creates a profile for an auth user that doesn't have one.
 * This is used to "fix" orphaned users who exist in auth.users
 * but don't have a corresponding core.profiles entry.
 */
export async function createProfileForUser(input: CreateProfileInput): Promise<Profile> {
  const { data, error } = await supabase
    .schema('core')
    .from('profiles')
    .insert({
      id: input.userId,
      email: input.email,
      full_name: input.fullName || null,
      is_super_admin: false, // Never create super admins via this method
    })
    .select('id, email, full_name, is_super_admin, created_at')
    .single();

  if (error) throw error;
  return data as Profile;
}

/**
 * Assigns a user to a tenant by creating a tenant membership.
 * Status defaults to 'approved' for super admin assignments.
 */
export async function assignUserToTenant(input: AssignTenantInput): Promise<TenantMembership> {
  const { data, error } = await supabase
    .schema('core')
    .from('tenant_memberships')
    .insert({
      user_id: input.userId,
      tenant_id: input.tenantId,
      status: input.status || 'approved',
    })
    .select()
    .single();

  if (error) throw error;
  return data as TenantMembership;
}

/**
 * Removes a user's tenant membership.
 */
export async function removeUserFromTenant(userId: string, tenantId: string): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('tenant_memberships')
    .delete()
    .eq('user_id', userId)
    .eq('tenant_id', tenantId);

  if (error) throw error;
}

/**
 * Updates the status of a tenant membership.
 */
export async function updateMembershipStatus(
  userId: string,
  tenantId: string,
  status: 'pending' | 'approved' | 'rejected'
): Promise<TenantMembership> {
  const { data, error } = await supabase
    .schema('core')
    .from('tenant_memberships')
    .update({ status })
    .eq('user_id', userId)
    .eq('tenant_id', tenantId)
    .select()
    .single();

  if (error) throw error;
  return data as TenantMembership;
}
